import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Layers, Plus, Pencil, Trash2 } from 'lucide-react'
import { SearchInput } from '../components/SearchInput'
import { getTraits, createTrait, updateTrait, deleteTrait, getIngredients, getCollections } from '../api/brainer'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'
import type { BTrait, BIngredient, BCollection } from '../types'

const blankForm = { title: '', description: '', ingredientId: '', collectionId: '', maxValue: '', minValue: '', maxLength: '', minLength: '' }
type Form = typeof blankForm

export function BrainerTraits() {
  const fmt = useDateFmt()
  const qc = useQueryClient()
  const { data: traits = [], isLoading } = useQuery({ queryKey: ['b-traits'], queryFn: () => getTraits().then((r) => r.data) })
  const { data: ingredients = [] } = useQuery({ queryKey: ['b-ingredients'], queryFn: () => getIngredients().then((r) => r.data) })
  const { data: collections = [] } = useQuery({ queryKey: ['b-collections'], queryFn: () => getCollections().then((r) => r.data) })

  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState<Form>(blankForm)
  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const create = useMutation({
    mutationFn: () => createTrait({
      title: form.title,
      ingredientId: form.ingredientId,
      collectionId: form.collectionId || undefined,
      description: form.description || undefined,
      maxValue: form.maxValue ? +form.maxValue : undefined,
      minValue: form.minValue ? +form.minValue : undefined,
      maxLength: form.maxLength ? +form.maxLength : undefined,
      minLength: form.minLength ? +form.minLength : undefined,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-traits'] }); setShowCreate(false); setForm(blankForm) },
  })

  const [editItem, setEditItem] = useState<BTrait | null>(null)
  const [editForm, setEditForm] = useState<Form>(blankForm)
  const openEdit = (t: BTrait) => {
    setEditItem(t)
    setEditForm({
      title: t.title, description: t.description ?? '', ingredientId: t.ingredientId,
      collectionId: t.collectionId ?? '',
      maxValue: t.maxValue?.toString() ?? '', minValue: t.minValue?.toString() ?? '',
      maxLength: t.maxLength?.toString() ?? '', minLength: t.minLength?.toString() ?? '',
    })
  }
  const setEF = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setEditForm((f) => ({ ...f, [k]: e.target.value }))

  const save = useMutation({
    mutationFn: () => updateTrait(editItem!.id, {
      title: editForm.title, description: editForm.description || undefined,
      ingredientId: editForm.ingredientId,
      collectionId: editForm.collectionId || undefined,
      maxValue: editForm.maxValue ? +editForm.maxValue : undefined,
      minValue: editForm.minValue ? +editForm.minValue : undefined,
      maxLength: editForm.maxLength ? +editForm.maxLength : undefined,
      minLength: editForm.minLength ? +editForm.minLength : undefined,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-traits'] }); setEditItem(null) },
  })

  const [search, setSearch] = useState('')
  const filtered = traits.filter((t: BTrait) =>
    [t.title, t.description ?? '', t.ingredient?.name ?? ''].some((v) => v.toLowerCase().includes(search.toLowerCase()))
  )

  const [deleteTarget, setDeleteTarget] = useState<BTrait | null>(null)
  const del = useMutation({
    mutationFn: (id: string) => deleteTrait(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-traits'] }); setDeleteTarget(null) },
  })

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Traits</h1>
          <p className="text-sm text-ink-dim mt-0.5">Typed field definitions with validation constraints</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search traits" />
          <Button icon={Plus} onClick={() => setShowCreate(true)}>New trait</Button>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
        ) : traits.length === 0 ? (
          <EmptyState icon={Layers} title="No traits" description="Create typed field definitions to attach to cups" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Constraints</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((t: BTrait) => (
                <tr key={t.id}>
                  <td>
                    <p className="font-medium text-ink">{t.title}</p>
                    {t.description && <p className="text-xs text-ink-dim mt-0.5">{t.description}</p>}
                  </td>
                  <td>
                    <div className="flex flex-col gap-0.5">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-paper-sunken text-ink-dim">
                        {t.ingredient?.name ?? ''}
                      </span>
                    </div>
                  </td>
                  <td className="text-xs text-ink-dim space-x-2">
                    {t.minLength != null && <span>minLen: {t.minLength}</span>}
                    {t.maxLength != null && <span>maxLen: {t.maxLength}</span>}
                    {t.minValue != null && <span>min: {t.minValue}</span>}
                    {t.maxValue != null && <span>max: {t.maxValue}</span>}
                    {t.minLength == null && t.maxLength == null && t.minValue == null && t.maxValue == null && <span></span>}
                  </td>
                  <td className="text-ink-dim">{fmt(t.createdAt)}</td>
                  <td>
                    <div className="flex items-center gap-3 justify-end">
                      <button onClick={() => openEdit(t)} className="text-ink-dim hover:text-ink transition-colors"><Pencil size={13} /></button>
                      <button onClick={() => setDeleteTarget(t)} className="text-ink-dim hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <Modal title="New trait" onClose={() => { setShowCreate(false); setForm(blankForm) }}>
          <TraitForm form={form} set={set} ingredients={ingredients} collections={collections} />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => { setShowCreate(false); setForm(blankForm) }}>Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={!form.title || !form.ingredientId || create.isPending} loading={create.isPending}>
              Create
            </Button>
          </div>
        </Modal>
      )}

      {editItem && (
        <Modal title="Edit trait" onClose={() => setEditItem(null)}>
          <TraitForm form={editForm} set={setEF} ingredients={ingredients} collections={collections} />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={!editForm.title || save.isPending} loading={save.isPending}>
              Save
            </Button>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete trait"
          message={`Delete trait "${deleteTarget.title}"?`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => del.mutate(deleteTarget.id)}
          loading={del.isPending}
        />
      )}
    </div>
  )
}

function TraitForm({ form, set, ingredients, collections }: { form: Form; set: (k: keyof Form) => (e: any) => void; ingredients: BIngredient[]; collections: BCollection[] }) {
  const selectedIngredient = ingredients.find((i) => i.id === form.ingredientId)
  const isArray = selectedIngredient?.name?.toLowerCase() === 'array'

  return (
    <div className="space-y-4">
      <Field label="Title *">
        <input value={form.title} onChange={set('title')} placeholder="e.g. First Name"
          className="field" />
      </Field>
      <Field label="Ingredient (type) *">
        <Select
          value={form.ingredientId}
          onValueChange={(v) => set('ingredientId')({ target: { value: v } })}
          placeholder="Select ingredient"
        >
          {ingredients.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}{i.description ? `  ${i.description}` : ''}</SelectItem>)}
        </Select>
      </Field>
      {isArray && (
        <Field label="Collection *">
          <Select
            value={form.collectionId}
            onValueChange={(v) => set('collectionId')({ target: { value: v } })}
            placeholder="Select collection"
          >
            {collections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.description ? `  — ${c.description}` : ''}</SelectItem>)}
          </Select>
        </Field>
      )}
      <Field label="Description">
        <input value={form.description} onChange={set('description')} placeholder="Optional"
          className="field" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Min length">
          <input type="number" value={form.minLength} onChange={set('minLength')} min="0"
            className="field" />
        </Field>
        <Field label="Max length">
          <input type="number" value={form.maxLength} onChange={set('maxLength')} min="1"
            className="field" />
        </Field>
        <Field label="Min value">
          <input type="number" value={form.minValue} onChange={set('minValue')}
            className="field" />
        </Field>
        <Field label="Max value">
          <input type="number" value={form.maxValue} onChange={set('maxValue')}
            className="field" />
        </Field>
      </div>
    </div>
  )
}
